import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import App from './App.jsx'
import PlatformApp from './PlatformApp.jsx'
import { isDhruvDeployment } from './config/deploymentProfile'
import { store } from './store/store'
import ScrollToTop from './components/ScrollToTop'
import { scrollAppToTop } from './utils/scrollAppToTop'
import './styles/workspace-theme.scss'
import './styles/index.scss'

import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap-icons/font/bootstrap-icons.css'
import 'react-toastify/dist/ReactToastify.css'
import './styles/toasts.scss'
import './styles/flat-ui.scss'

const RootApp = isDhruvDeployment() ? PlatformApp : App

if (isDhruvDeployment() && typeof document !== 'undefined') {
  document.documentElement.classList.add('dhruv-deployment')
  document.body.classList.add('workspace-theme')
}

/**
 * A prior production build registers a service worker on localhost.
 * In Vite dev that SW can serve stale index.html → 404 on old hashed chunks (blank page).
 */
async function prepareDevEnvironment() {
  if (!import.meta.env.DEV) return
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((reg) => reg.unregister()))
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch {
    // Non-fatal — app should still mount
  }
}

function mountApp() {
  if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
  }

  ReactDOM.createRoot(document.getElementById('root')).render(
    <Provider store={store}>
      <BrowserRouter>
        <ScrollToTop />
        <RootApp />
        <ToastContainer
          position="top-right"
          autoClose={3200}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss={false}
          pauseOnHover
          limit={4}
          theme="light"
          toastClassName="ws-toast"
        />
      </BrowserRouter>
    </Provider>
  )

  scrollAppToTop()
}

prepareDevEnvironment().then(mountApp)

if (import.meta.env.PROD) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({ immediate: true })
  })
}
